import { PlatformEntity } from '@provision/entity-types';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

const GET_PLATFORM_ENTITY_QUERY = gql`
  query GetPlatformEntity($params: QueryParams) @entity(name: "PlatformEntity") {
    getPlatformEntity(queryParams: $params) {
      records {
        id
        entity
        displayName
      }
    }
  }
`;

interface GetPlatformEntityQuery {
  getPlatformEntity: Records<PlatformEntity>;
}

export function usePlatformEntities(
  params?: {
    filters?: ConditionInput[];
  },
  options?: QueryHookOptions<GetPlatformEntityQuery>,
) {
  const { data, ...rest } = useQuery<GetPlatformEntityQuery, QueryVariables>(
    GET_PLATFORM_ENTITY_QUERY,
    {
      ...options,
      variables: {
        params: { filters: params?.filters, sorting: { attribute: 'displayName' } },
      },
    },
  );

  const platformEntities = useMemo(
    () => data?.getPlatformEntity?.records ?? [],
    [data?.getPlatformEntity],
  );

  return {
    platformEntities,
    ...rest,
  };
}

export function usePlatformEntityOptions(
  params?: {
    filters?: ConditionInput[];
  },
  options?: QueryHookOptions<GetPlatformEntityQuery>,
) {
  const { platformEntities, ...rest } = usePlatformEntities(params, options);

  const platformEntityOptions = useMemo(
    () =>
      platformEntities.map((platformEntity) => ({
        id: platformEntity.id,
        label: platformEntity.displayName,
      })),
    [platformEntities],
  );

  return {
    platformEntityOptions,
    ...rest,
  };
}
